import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthApiService } from '../services/auth/auth-api.service';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { TimeoutError } from 'rxjs';
import { isNativePlatform } from '../utils/util';

const REFRESH_RETRY_ATTEMPTED = new HttpContextToken<boolean>(() => false);

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private authApiService = inject(AuthApiService);
  private router = inject(Router);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse | TimeoutError) => {
        if (error instanceof TimeoutError) {
          console.error(`[ErrorInterceptor] Request timed out: ${request.method} ${request.url}`);

          const timeoutError = new HttpErrorResponse({
            status: 408,
            statusText: 'Request Timeout',
            url: request.url,
          });
          return throwError(() => timeoutError);
        }

        if (error.status === 401 && this.shouldAttemptRefresh(request)) {
          return this.refreshAndRetry(request, next, error);
        }

        if (error.status === 401) {
          this.handleUnauthorized(request);
        }

        switch (error.status) {
          case 403:
            // 403 Forbidden - User doesn't have permission
            // The component should handle showing an appropriate message
            console.warn('[ErrorInterceptor] Access forbidden:', request.url);
            break;

          case 404:
            // 404 Not Found - Resource doesn't exist
            console.warn('[ErrorInterceptor] Resource not found:', request.url);
            break;

          case 500:
            // 500 Internal Server Error - Something went wrong on the server
            console.error('[ErrorInterceptor] Server error:', error.message);
            break;

          case 0:
            // Network error - Server is unreachable
            console.error('[ErrorInterceptor] Network error - server may be down');
            break;

          default:
            console.error('[ErrorInterceptor] HTTP error:', error.status, error.message);
        }
        return throwError(() => error);
      }),
    );
  }

  private shouldAttemptRefresh(request: HttpRequest<unknown>): boolean {
    if (request.context.get(REFRESH_RETRY_ATTEMPTED)) {
      return false;
    }

    return !this.isAuthEndpoint(request.url);
  }

  private isAuthEndpoint(url: string): boolean {
    return (
      url.endsWith('/auth/login') || url.endsWith('/auth/refresh') || url.endsWith('/auth/logout')
    );
  }

  private refreshAndRetry(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    originalError: HttpErrorResponse,
  ): Observable<HttpEvent<unknown>> {
    return this.refreshTokenSingleFlight().pipe(
      switchMap(() => {
        const retryRequest = this.buildRetriedRequest(request);
        return next.handle(retryRequest);
      }),
      catchError((refreshError) => {
        this.forceLogoutAndRedirect();
        return throwError(() =>
          refreshError instanceof HttpErrorResponse ? refreshError : originalError,
        );
      }),
    );
  }

  private refreshTokenSingleFlight(): Observable<unknown> {
    return this.authApiService.refreshSessionSingleFlight();
  }

  private buildRetriedRequest(request: HttpRequest<unknown>): HttpRequest<unknown> {
    const freshToken = this.authApiService.getToken();

    const headersToSet: Record<string, string> = {};

    if (freshToken) {
      headersToSet['Authorization'] = `Bearer ${freshToken}`;
    }

    if (isNativePlatform()) {
      const clientId = this.authApiService.getClientId();
      if (clientId) {
        headersToSet['X-Client-ID'] = clientId;
      }
    }

    return request.clone({
      context: request.context.set(REFRESH_RETRY_ATTEMPTED, true),
      setHeaders: headersToSet,
    });
  }

  private handleUnauthorized(request: HttpRequest<unknown>): void {
    if (this.isAuthEndpoint(request.url)) {
      return;
    }

    if (request.context.get(REFRESH_RETRY_ATTEMPTED)) {
      this.forceLogoutAndRedirect();
      return;
    }

    // Request was not retried (no refresh policy for this request path).
    this.forceLogoutAndRedirect();
  }

  private forceLogoutAndRedirect(): void {
    console.warn('[ErrorInteceptor] Unauthorized - logging out user');
    this.authApiService.logout();

    const currentUrl = this.router.url;
    const publicRoutes = ['/auth'];

    if (!publicRoutes.some((route) => currentUrl.startsWith(route))) {
      this.router.navigate(['/auth'], {
        queryParams: {
          return: currentUrl,
        },
      });
    }
  }
}
